/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AOMGH5PageViewPlugin
 */

import { ITracker } from '@omega-tracker/core/src/com/didichuxing/tracker/ITracker';
import { IContext } from 'com/didichuxing/context/IContext';
import { ITrackerConfiguration } from 'com/didichuxing/tracker/config/ITrackerConfiguration';
import { ITrackerAdvancedContext } from 'com/didichuxing/tracker/h5/context/ITrackerAdvancedContext';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
import OMGH5PageViewEvent from 'com/didichuxing/tracker/h5/plugins/events/OMGH5PageViewEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';

const PV_TYPE = {
    hash: 'hash',
    popstate: 'popstate',
    normal: 'normal'
};

export default abstract class AOMGH5PageViewPlugin extends AReportingStrategyPlugin {

    /**
     * 访问过的url栈。
     * @type {string[]}
     */
    private urlStatck: string[] = [document.referrer];

    /**
     * @constructor
     */
    constructor(tracker: ITracker) {
        super(tracker);
        this.reportPVUnderHashChange = this.reportPVUnderHashChange.bind(this);
        this.reportPVUnderPopstateChange = this.reportPVUnderPopstateChange.bind(this);
        this.urlStatck.push(location.href);
    }

    /**
     * @override
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_PAGE_VIEW;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {ITrackerConfiguration}
     */
    protected get initialzedContext(): IContext {
        const context: ITrackerAdvancedContext = this.getContext() as ITrackerAdvancedContext;
        return {
            ...super.initialzedContext,
            autoSendPageView: typeof context.autoSendPageView === 'boolean' ? context.autoSendPageView : true
        } as IContext;
    }

    /**
     * 上报PV数据。
     */
    protected reportPV(pvType: string): void {
        this.getTracker().setConfig({
            url: window.location.href
        });
        let cityId;
        const context = this.getContext() as any;
        if (context && context.cityId) {
            cityId = context.cityId;
        }
        this.report({
            eventId: PluginUtils.EVENT_ID_PAGE_VIEW,
            attrs: {
                // 修复每次发送的reffer是当前的url
                referrer: this.urlStatck.length > 1 ? this.urlStatck[this.urlStatck.length - 2] : this.urlStatck[0],
                pvType,
                omg_ci: cityId
            }
        });
    }

    /**
     * hash change上报pv
     */
    protected reportPVUnderHashChange(): void {
        this.reportPV(PV_TYPE.hash);
    }

    /**
     * popstate change上报pv
     */
    protected reportPVUnderPopstateChange(): void {
        this.reportPV(PV_TYPE.popstate);
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
        const context: ITrackerAdvancedContext = this.getContext() as ITrackerAdvancedContext;
        if (context.autoSendPageView) { // 自动上报PV数据
            this.reportPV(PV_TYPE.normal);
            if (context.hashRouterEnable) { // 监控hash history change
                window.addEventListener('hashchange', this.reportPVUnderHashChange);
            }
            if (context.browserRouterEnable) { // 监控browser popstate change
                window.addEventListener('popstate', this.reportPVUnderPopstateChange);
            }
        }
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        while (this.urlStatck.length) {
            this.urlStatck.pop();
        }
        window.removeEventListener('hashchange', this.reportPVUnderHashChange);
        window.removeEventListener('popstate', this.reportPVUnderPopstateChange);
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        return new OMGH5PageViewEvent(eventArgs.attrs, this.getContext());
    }
}
